import React, { useState, useRef } from "react";
import cn from "classnames";
import { Bug, HelpCircle, Lightbulb, XIcon, Send, Loader } from "lucide-react";
import style from "./ReportPanel.module.scss";
import CustomUploadFileMultiple from "../orders-aviation/components/custom-upload/CustomUploadFileMultiple";
import { useSelector } from "react-redux";
import Report from "../../services/Report";
import { toast } from "react-toastify";
import useScreenshotCapture from "../../hooks/useScreenshotCapture";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const ReportPanel = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isAnalyticsPage = /^\/analytics-new\/?$/.test(pathname);
  const { user } = useSelector(state => state);
  const [isOpen, setIsOpen] = useState(false);
  const [activeType, setActiveType] = useState("bug");
  const [files, setFiles] = useState([]);
  const [screenshot, setScreenshot] = useState(null);
  const [formData, setFormData] = useState({ type: "bug", description: "" });
  const [isLoading, setIsLoading] = useState(false);

  const panelRef = useRef(null);
  const { captureAndSetScreenshot } = useScreenshotCapture();

  const reportTypes = {
    bug: {
      icon: Bug,
      label: t("reportPanel.types.bug.label"),
      colorClass: style.colorBug,
      bgColorClass: style.bgBug,
      placeholder: t("reportPanel.types.bug.placeholder"),
    },
    question: {
      icon: HelpCircle,
      label: t("reportPanel.types.question.label"),
      colorClass: style.colorQuestion,
      bgColorClass: style.bgQuestion,
      placeholder: t("reportPanel.types.question.placeholder"),
    },
    offer: {
      icon: Lightbulb,
      label: t("reportPanel.types.offer.label"),
      colorClass: style.colorSuggestion,
      bgColorClass: style.bgSuggestion,
      placeholder: t("reportPanel.types.offer.placeholder"),
    },
  };

  const handleSubmit = async () => {
    if (!formData.description.trim()) {
      toast.error(t("reportPanel.errorEmptyDescription"));
      return;
    }

    const reportData = {
      type: formData.type,
      description: formData.description,
      metadata: JSON.stringify({
        url: window.location.href,
        browser: navigator.userAgent,
        userId: user?.id,
        role: user?.type,
        networkStatus: navigator.onLine ? "online" : "offline",
      }),
    };

    setIsLoading(true);

    try {
      const response = await Report.sendBugReport(reportData);
      const reportId = response.data.id;

      if (screenshot && screenshot.name) {
        await uploadFile(reportId, screenshot, screenshot.name);
      }
      if (files.length > 0) {
        await Promise.all(files.map(file => uploadFile(reportId, file, file.name)));
      }

      toast.success(t("reportPanel.successReportSent"));
      resetForm();
      setIsOpen(false);
    } catch (error) {
      console.error("Error sending report:", error);
      toast.error(t("reportPanel.errorGeneric"));
    } finally {
      setIsLoading(false);
    }
  };

  const uploadFile = async (bugReportId, file, fileName) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("FileName", fileName);
    formData.append("bugReportId", bugReportId);

    try {
      await Report.sendBugReportDocument(formData);
      toast.info(t("reportPanel.successFileUploaded"));
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(t("reportPanel.errorFileUpload"));
    }
  };

  const resetForm = () => {
    setFormData({ type: "bug", description: "" });
    setFiles([]);
    setScreenshot({});
    setActiveType("bug");
  };

  const handleTypeSelect = async (type) => {
    const screenshot = await captureAndSetScreenshot();
    if (screenshot) setScreenshot(screenshot);
    setFormData(prev => ({ ...prev, type }));
    setActiveType(type);
    setIsOpen(true);
  };

  const closePanel = () => {
    setIsOpen(false);
    resetForm();
  };

  const currentType = reportTypes[activeType];
  const IconComponent = currentType.icon;
  const isFormValid = formData.description.trim() && !isLoading;

  return (
    <div className={cn(style.panelRoot)}>
      {isOpen && <div className={style.overlay} onClick={closePanel} />}

      <div className={cn(style.triggerContainer, { [style.triggerContainerLeft]: isAnalyticsPage })}>
        {Object.entries(reportTypes)
          .filter(([type]) => type === "question")
          .map(([type, config]) => {
            const Icon = config.icon;
            return (
              <button
                key={type}
                className={cn(style.triggerButton, {
                  [config.colorClass]: activeType === type && isOpen,
                  [config.bgColorClass]: activeType === type && isOpen,
                  [style.triggerButtonInactive]: !(activeType === type && isOpen),
                })}
                disabled={isLoading}
              >
                <span
                  className={style.iconTrigger}
                  onClick={() => handleTypeSelect(type)}
                >
                  <Icon size={24} />
                </span>
                <div className={style.tooltip}>{config.label}</div>
              </button>
            );
          })}
      </div>

      <div ref={panelRef} className={cn(style.sidePanel, { [style.open]: isOpen })}>
        {/* Header */}
        <div className={cn(style.header, currentType.bgColorClass)}>
          <div className={style.headerTop}>
            <div className={style.headerTitle}>
              <div className={cn(style.iconWrapper, currentType.colorClass, style.activeType)}>
                <IconComponent size={24} />
              </div>
              <h2 className={style.title}>{currentType.label}</h2>
            </div>
            <button onClick={closePanel} className={style.closeButton} disabled={isLoading}>
              <XIcon size={20} />
            </button>
          </div>

          {/* Type Selector */}
          <div className={style.typeSelector}>
            {Object.entries(reportTypes).map(([type, config]) => (
              <button
                key={type}
                onClick={() => {
                  setActiveType(type);
                  setFormData(prev => ({ ...prev, type }));
                }}
                className={cn(style.typeButton, {
                  [style.activeType]: activeType === type,
                  [config.colorClass]: activeType === type,
                })}
                disabled={isLoading}
              >
                <config.icon size={16} />
                {config.label}
              </button>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className={style.form}>
          <div className={style.sidePanel_content}>
            {/* Description */}
            <div className={style.formGroup}>
              <label className={style.label}>{t("reportPanel.descriptionLabel")}</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={currentType.placeholder}
                rows={6}
                required
                className={style.textarea}
                disabled={isLoading}
              />
            </div>

            {/* File Upload */}
            <div className={style.formGroup}>
              <label className={style.label}>{t("reportPanel.attachFiles")}</label>
              <CustomUploadFileMultiple
                title={t("reportPanel.attachFilesTitle")}
                dragText={t("reportPanel.attachFilesDrag")}
                files={files}
                onUploadFiles={setFiles}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={!isFormValid}
            className={cn(style.submitButton, {
              [style.disabled]: !isFormValid,
              [currentType.colorClass]: isFormValid,
            })}
          >
            {isLoading ? (
              <>
                <Loader size={20} className="animate-spin" />
                {t("reportPanel.submitting")}
              </>
            ) : (
              <>
                <Send size={20} />
                {t("reportPanel.submit")}
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <div className={style.footer}>
          <p>{t("reportPanel.footerNote")}</p>
        </div>
      </div>
    </div>
  );
};

export default ReportPanel;
